import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Typography,
  OutlinedInput,
  Divider,
  Autocomplete,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { Address } from '../types';
import { COUNTRY_CODES } from '../../../auth/constants/countryCodes';
import { City, Country, ICity, ICountry, IState, State } from 'country-state-city';


type AddressModalProps = {
  children?: React.ReactNode;
  open: boolean;
  onClose: () => void;
  onSave: (address: Omit<Address, 'id'>) => Promise<void>;
  initialData?: Address;
  isLoading?: boolean;
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    height: 48,
    borderRadius: '12px',
    backgroundColor: '#fff',
    '& fieldset': { borderColor: '#111' },
    '&:hover fieldset': { borderColor: '#111' },
    '&.Mui-focused fieldset': { borderColor: '#111', borderWidth: 1 },
    '&.Mui-disabled fieldset': { borderColor: '#111' },
  },
  '& .MuiOutlinedInput-input': {
    padding: '12px 14px',
    fontSize: 14,
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 0,
  },
};

const selectInAdornmentSx = {
  minWidth: 78,
  '& .MuiSelect-select': {
    padding: '0 10px 0 12px',
    display: 'flex',
    alignItems: 'center',
    height: 48,
    fontSize: 14,
  },
};

export const AddressModal: React.FC<AddressModalProps> = ({
  open,
  onClose,
  onSave,
  initialData,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<Address>({
    title: '',
    firstName: '',
    lastName: '',
    phoneCode: '+66',
    phoneNumber: '',
    country: '',
    address: '',
    province: '',
    district: '',
    postcode: '',
    isDefault: false,
  });

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || '',
        firstName: initialData.firstName || '',
        lastName: initialData.lastName || '',
        phoneCode: initialData.phoneCode || '+66',
        phoneNumber: initialData.phoneNumber || '',
        country: initialData.country || '',
        address: initialData.address || '',
        province: initialData.province || '',
        district: initialData.district || '',
        postcode: initialData.postcode || '',
        isDefault: initialData.isDefault || false,
      });
    } else {
      setFormData({
        title: '',
        firstName: '',
        lastName: '',
        phoneCode: '+66',
        phoneNumber: '',
        country: '',
        address: '',
        province: '',
        district: '',
        postcode: '',
        isDefault: false,
      });
    }
    setFieldErrors({});
  }, [initialData, open]);

  const allCountries = useMemo(() => Country.getAllCountries(), []);

  const states = useMemo(() => {
    if (!formData.country) return [];
    return State.getStatesOfCountry(formData.country);
  }, [formData.country]);

  const cities = useMemo(() => {
    if (!formData.country || !formData.province) return [];
    const stateObj = states.find((s) => s.name === formData.province);
    if (!stateObj) return [];
    return City.getCitiesOfState(formData.country, stateObj.isoCode);
  }, [formData.country, formData.province, states]);

  const handleText =
    (field: keyof Address) => (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value;

      if (field === 'phoneNumber' || field === 'postcode') {
        value = value.replace(/[^0-9]/g, '');
      }

      setFormData((prev: Address) => ({ ...prev, [field]: value }));
      setFieldErrors((prev) => ({ ...prev, [field]: '' }));
    };

  const handleSelect = (field: keyof Address) => (e: any) => {
    const value = e.target.value as string;

    setFieldErrors((prev) => ({ ...prev, [field]: '' }));

    if (field === 'country') {
    setFormData((prev: Address) => ({ ...prev, country: value, province: '', district: '', postcode: '' }));
      return;
    }
    if (field === 'province') {
      setFormData((prev: Address) => ({ ...prev, province: value, district: '', postcode: '' }));
      return;
    }
    if (field === 'district') {
      setFormData((prev: Address) => ({ ...prev, district: value, postcode: '' }));
      return;
    }

    setFormData((prev: Address) => ({ ...prev, [field]: value }));
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.firstName.trim()) errors.firstName = 'Please enter your first name';
    if (!formData.lastName.trim()) errors.lastName = 'Please enter your last name';
    if (formData.phoneNumber.replace(/\s/g, '').length < 7) errors.phoneNumber = 'Phone number must be at least 7 digits';
    if (!formData.country) errors.country = 'Please select country';
    if (!formData.address.trim()) errors.address = 'Please enter address';
    if (!formData.province) errors.province = 'Please select city/province';
    if (!formData.district) errors.district = 'Please select district';
    if (!formData.postcode.trim()) errors.postcode = 'Please enter postcode';

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onClickSave = async () => {
    if (!validate()) return;
    await onSave(formData);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 0 } }}
    >
      <Box sx={{ position: 'absolute', top: 10, right: 10, zIndex: 1 }}>
        <IconButton onClick={onClose} size="small" sx={{ color: 'text.primary' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 5, px: { xs: 3, sm: 6 }, pb: 6 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {/* Address Name + Default */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 300px', maxWidth: { md: '58.33%' } }}>
              <TextField
                fullWidth
                value={formData.title}
                onChange={handleText('title')}
                placeholder="Address name"
                sx={fieldSx}
              />
            </Box>

            <Box sx={{ flex: '1 1 200px', maxWidth: { md: '41.66%' } }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isDefault}
                    onChange={() => setFormData((p: Address) => ({ ...p, isDefault: !p.isDefault }))}
                    sx={{ color: '#111', '&.Mui-checked': { color: '#111' } }}
                  />
                }
                label="Set as default address"
                sx={{ ml: 0, '& .MuiFormControlLabel-label': { fontSize: 14, color: '#111' } }}
              />
            </Box>
          </Box>

          {/* Personal Information */}
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 2 }}>
              Personal Information <Typography component="span" sx={{ color: '#d00' }}>*</Typography>
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {/* Ms | First name (single box) */}
              <Box sx={{ flex: '1 1 300px', maxWidth: { md: '50%' } }}>
                <TextField
                  fullWidth
                  value={formData.firstName}
                  onChange={handleText('firstName')}
                  placeholder="First Name"
                  error={!!fieldErrors.firstName}
                  helperText={fieldErrors.firstName}
                  sx={fieldSx}
                />
              </Box>

              <Box sx={{ flex: '1 1 300px', maxWidth: { md: '50%' } }}>
                <TextField
                  fullWidth
                  value={formData.lastName}
                  onChange={handleText('lastName')}
                  placeholder="Last name"
                  error={!!fieldErrors.lastName}
                  helperText={fieldErrors.lastName}
                  sx={fieldSx}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>

              {/* +66 | Phone (single box) */}
              <Box sx={{ flex: '1 1 300px', maxWidth: { md: '50%' } }}>
                <FormControl fullWidth sx={fieldSx}>
                  <OutlinedInput
                    notched
                    value={formData.phoneNumber}
                    onChange={handleText('phoneNumber')}
                    placeholder="Phone Number"
                    error={!!fieldErrors.phoneNumber}
                    inputProps={{ maxLength: 12 }}
                    startAdornment={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Select
                          variant="standard"
                          value={formData.phoneCode}
                          onChange={handleSelect('phoneCode')}
                          disableUnderline
                          sx={{ ...selectInAdornmentSx, minWidth: 90 }}
                        >
                          {COUNTRY_CODES.map((c) => (
                            <MenuItem key={c.code} value={c.code}>
                              {c.code}
                            </MenuItem>
                          ))}
                        </Select>

                        <Divider orientation="vertical" flexItem sx={{ borderColor: '#111' }} />
                      </Box>
                    }
                  />
                </FormControl>

                {!!fieldErrors.phoneNumber && (
                  <Typography sx={{ mt: 0.75, fontSize: 12, color: '#d32f2f' }}>
                    {fieldErrors.phoneNumber}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          {/* Address */}
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 2 }}>
              Address <Typography component="span" sx={{ color: '#d00' }}>*</Typography>
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box sx={{ flex: '1 1 300px', maxWidth: { md: '50%' } }}>
                <Autocomplete
                  options={allCountries}
                  getOptionLabel={(option: ICountry) => option.name}
                  value={allCountries.find((c) => c.isoCode === formData.country) || null}
                  onChange={(_e, newValue) => {
                    const value = newValue ? newValue.isoCode : '';
                    setFormData((prev) => ({ ...prev, country: value, province: '', district: '', postcode: '' }));
                    setFieldErrors((prev) => ({ ...prev, country: '' }));
                  }}
                  slotProps={{ listbox: { style: { maxHeight: 300 } } }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Country"
                      error={!!fieldErrors.country}
                      helperText={fieldErrors.country}
                      InputLabelProps={{ shrink: true }}
                      sx={fieldSx}
                    />
                  )}
                />
              </Box>

              <Box sx={{ flex: '1 1 300px', maxWidth: { md: '50%' } }}>
                <TextField
                  fullWidth
                  value={formData.address}
                  onChange={handleText('address')}
                  placeholder="Address"
                  error={!!fieldErrors.address}
                  helperText={fieldErrors.address}
                  sx={fieldSx}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>

              <Box sx={{ flex: '1 1 300px', maxWidth: { md: '50%' } }}>
                <Autocomplete
                  options={states}
                  getOptionLabel={(option: IState) => option.name}
                  value={states.find((s) => s.name === formData.province) || null}
                  onChange={(_e, newValue) => {
                    const value = newValue ? newValue.name : '';
                    setFormData((prev) => ({ ...prev, province: value, district: '', postcode: '' }));
                    setFieldErrors((prev) => ({ ...prev, province: '' }));
                  }}
                  disabled={!formData.country}
                  slotProps={{ listbox: { style: { maxHeight: 300 } } }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Province/State"
                      error={!!fieldErrors.province}
                      helperText={fieldErrors.province}
                      InputLabelProps={{ shrink: true }}
                      sx={fieldSx}
                    />
                  )}
                />
              </Box>

              <Box sx={{ flex: '1 1 300px', maxWidth: { md: '50%' } }}>
                <Autocomplete
                  options={cities}
                  getOptionLabel={(option: ICity) => option.name}
                  value={cities.find((c) => c.name === formData.district) || null}
                  onChange={(_e, newValue) => {
                    const value = newValue ? newValue.name : '';
                    setFormData((prev) => ({ ...prev, district: value, postcode: '' }));
                    setFieldErrors((prev) => ({ ...prev, district: '' }));
                  }}
                  disabled={!formData.province}
                  slotProps={{ listbox: { style: { maxHeight: 300 } } }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="District"
                      error={!!fieldErrors.district}
                      helperText={fieldErrors.district}
                      InputLabelProps={{ shrink: true }}
                      sx={fieldSx}
                    />
                  )}
                />
              </Box>

              <Box sx={{ flex: '1 1 300px', maxWidth: { md: '50%' }} } />

              <Box sx={{ flex: '1 1 300px', maxWidth: { md: '50%' } }}>
                <TextField
                  fullWidth
                  value={formData.postcode}
                  onChange={handleText('postcode')}
                  placeholder="Postcode"
                  disabled={!formData.district}
                  error={!!fieldErrors.postcode}
                  helperText={fieldErrors.postcode}
                  sx={fieldSx}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 10 }}
                />
              </Box>
            </Box>
          </Box>

          {/* Save */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <Button
              onClick={onClickSave}
              variant="contained"
              disabled={
                isLoading ||
                !formData.firstName.trim() ||
                !formData.lastName.trim() ||
                formData.phoneNumber.replace(/\s/g, '').length < 7 ||
                !formData.country ||
                !formData.address.trim() ||
                !formData.province ||
                !formData.district ||
                !formData.postcode.trim()
              }
              sx={{
                width: 220,
                height: 52,
                borderRadius: '14px',
                backgroundColor: '#111',
                textTransform: 'none',
                fontSize: 16,
                fontWeight: 500,
                '&:hover': { backgroundColor: '#111' },
                '&:disabled': { backgroundColor: '#ccc', color: '#666' },
              }}
            >
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AddressModal;